import Seo from "@/components/base/Seo";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/base/BackToTop";
import Breadcrumb from "@/components/base/Breadcrumb";
import { characters } from "@/mocks/characters";

const collectionLd = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  name: "洞洞杂货铺漫画角色图鉴",
  description: "洞洞杂货铺漫画主要角色档案：店主阿洞、洞洞猫、失眠人小夜、邮差鸽子先生及其角色关系。",
};

const breadcrumbLd = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "首页", item: "https://comworld1.github.io/" },
    { "@type": "ListItem", position: 2, name: "角色图鉴", item: "https://comworld1.github.io/characters" },
  ],
};

export default function Characters() {
  return (
    <>
      <Seo
        title="洞洞杂货铺漫画角色图鉴 - 角色关系与人物档案"
        description="洞洞杂货铺漫画角色图鉴：店主阿洞、洞洞猫、失眠人小夜、邮差鸽子先生等主要角色档案，梳理人物关系与每个角色背后的伏笔设定。"
        keywords="洞洞杂货铺角色,洞洞杂货铺人物,洞洞杂货铺角色关系,洞洞杂货铺漫画角色"
        canonicalPath="/characters"
        jsonLd={[collectionLd, breadcrumbLd]}
      />
      <Navbar />
      <main>
        <div className="mx-auto w-full max-w-6xl px-4 pt-8 md:px-6">
          <Breadcrumb items={[{ label: "角色图鉴" }]} />
        </div>

        <div className="mx-auto w-full max-w-6xl px-4 py-8 md:px-6 md:py-10">
          <div className="max-w-3xl">
            <span className="inline-flex rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-800 whitespace-nowrap">
              人物档案
            </span>
            <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
              杂货铺里的他们，与洞洞的秘密
            </h1>
            <p className="mt-4 text-sm leading-7 text-foreground-700 md:text-base">
              每一位角色都用自己的方式与洞洞相处：有人在倾听，有人在守门，
              有人在送信，有人在寻找。读懂他们之间的关系，
              是理解整部漫画故事背景和核心谜题的一把钥匙。
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {characters.map((char) => (
              <article
                key={char.id}
                className="flex flex-col overflow-hidden rounded-2xl bg-background-100/70 ring-1 ring-background-200/70 transition-transform duration-300 hover:-translate-y-1"
              >
                <div className="relative h-64 w-full overflow-hidden">
                  <img
                    src={char.image}
                    alt={`${char.name}·${char.role}角色插画`}
                    className="h-full w-full object-cover transition-transform duration-500 hover:scale-105"
                  />
                  <span
                    className={`absolute left-3 top-3 rounded-full px-2.5 py-1 text-xs font-medium text-background-50 ${
                      char.accent === "primary"
                        ? "bg-primary-500"
                        : char.accent === "accent"
                        ? "bg-accent-500"
                        : "bg-secondary-500"
                    }`}
                  >
                    {char.trait}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <h2 className="font-heading text-lg font-bold text-foreground-950">
                    {char.name}
                  </h2>
                  <p className="mt-0.5 text-xs font-medium text-primary-700">{char.role}</p>
                  <p className="mt-2 text-sm font-medium text-foreground-800">{char.tagline}</p>
                  <p className="mt-3 flex-1 text-sm leading-6 text-foreground-700">{char.desc}</p>
                </div>
              </article>
            ))}
          </div>

          <div className="mt-12 rounded-2xl bg-background-100/70 p-6 ring-1 ring-background-200/70 md:p-8">
            <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
              角色与洞洞的关系速览
            </h2>
            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {[
                { name: "店主「阿洞」", rel: "洞洞的倾听者" },
                { name: "洞洞猫", rel: "洞洞的守门者" },
                { name: "失眠人「小夜」", rel: "洞洞的旅人" },
                { name: "邮差「鸽子先生」", rel: "洞洞的邮差" },
              ].map((item) => (
                <div key={item.name} className="rounded-xl bg-background-50 p-4 ring-1 ring-background-200/70">
                  <p className="font-heading text-sm font-bold text-foreground-950">{item.name}</p>
                  <p className="mt-1.5 text-xs text-accent-700">{item.rel}</p>
                </div>
              ))}
            </div>
            <p className="mt-6 text-sm leading-7 text-foreground-700">
              想知道这些关系背后藏着的伏笔？不妨去
              <a href="/worldview" className="mx-1 font-medium text-primary-700 underline underline-offset-4">
                世界观解析
              </a>
              与
              <a href="/settings" className="mx-1 font-medium text-primary-700 underline underline-offset-4">
                设定百科
              </a>
              里继续深挖。
            </p>
          </div>
        </div>
      </main>
      <Footer />
      <BackToTop />
    </>
  );
}